import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Loading from '@components/loading'
import FormTitle from '@components/text/formTitle'
import FormInput from '@components/inputs/formInput'
import RequiredFields from '@components/requiredFields'
import {
  cpfMask,
  dateMask,
  onlyNotNumbersMask,
  phoneMask,
  insuranceMask,
  onlyNumbersMask
} from '@util/stringUtil'

import {
  Container,
  TitleContainer,
  ButtonContainer,
  FormFields,
  Section,
  SaveButton
} from './styles'

import RadioButtons from '@components/inputs/radioButtons'
import { saveBeneficiary as saveBeneficiaryAction } from '@actions/beneficiary'
import { validateBeneficiaryFields, validateUser } from '@util/validationUtil'
import InsuranceType from '@util/InsuranceTypeEnum'
import { WindowContext } from '@components/windowContextProvider'

const AddBeneficiaryModal = ({
  saveBeneficiary,
  isOpen,
  setClose,
  company,
  programme
}) => {
  const [loading, setLoading] = useState(false)
  const [beneficiary, setBeneficiary] = useState({})
  const [errors, setErrors] = useState({})
  const {
    name,
    cpf,
    birthdate,
    insuranceNumber,
    sex,
    email,
    phoneNumber,
    insuranceType,
  } = beneficiary
  const { clientHeight } = React.useContext(WindowContext)

  function checkOnUnfocus(label, value) {
    const error = validateBeneficiaryFields[label](value)
    if (error) {
      setErrors({
        ...errors,
        [label]: error
      })
    } else {
      const aux = errors
      delete aux[label]
      setErrors({ ...aux })
    }
  }

  const onChange = (field, e) => {
    setBeneficiary({
      ...beneficiary,
      [field]: e.target.value,
    })
  }

  const onChangeName = (e) => {
    const newValue = e.target.value
    setBeneficiary({
      ...beneficiary,
      name: newValue,
    })

    setTimeout(() => {
      const maskedValue = onlyNotNumbersMask(newValue)
      setBeneficiary((prevState) => ({
        ...prevState,
        name: maskedValue,
      }))
    }, 0)
  }

  const onChangeInsuranceType = (field, e) => {
    setBeneficiary({
      ...beneficiary,
      [field]: e,
    })
  }

  const onChangeSex = (e) => {
    setBeneficiary({
      ...beneficiary,
      sex: e === 'Masculino' ? 'M' : 'F',
    })
  }

  const onError = ({ message }) => {
    setLoading(false)
    if (message) {
      const errorLabel = message.split(':')
      setErrors({ ...errors, [errorLabel[0]]: errorLabel[1] })
    } else {
      setClose()
    }
  }

  const handleClickSave = () => {
    setLoading(true)
    const data = { ...beneficiary, company, programme }
    if (insuranceNumber) {
      data.insuranceNumber = onlyNumbersMask(insuranceNumber)
    }
    if (insuranceType) {
      data.insuranceTypeId = InsuranceType[data.insuranceType].id
    }
    saveBeneficiary(data, onError)
  }

  return (
    <Container isOpen={isOpen} clientHeight={clientHeight}>
      <Section>
        <TitleContainer>
          <FormTitle
            title="Adicionar novo beneficiário"
            onClickGoBack={setClose}
          />
        </TitleContainer>
        <FormFields>
          <FormInput
            type="text"
            value={name}
            onChange={onChangeName}
            onBlur={() => checkOnUnfocus('name', name)}
            label="Nome"
            error={errors.name}
            required
          />
          <FormInput
            type="text"
            value={cpfMask(cpf)}
            onChange={(e) => onChange('cpf', e)}
            onBlur={() => checkOnUnfocus('cpf', cpf)}
            label="CPF"
            error={errors.cpf}
            required
          />
          <FormInput
            type="text"
            value={dateMask(birthdate)}
            onChange={(e) => onChange('birthdate', e)}
            label="Data de nascimento"
            onBlur={() => checkOnUnfocus('birthdate', birthdate)}
            error={errors.birthdate}
            required
          />
          <FormInput
            type="text"
            value={email}
            onChange={(e) => onChange('email', e)}
            onBlur={() => checkOnUnfocus('email', email)}
            label="Email"
            error={errors.email}
          />
          <FormInput
            type="text"
            value={phoneMask(phoneNumber)}
            onChange={(e) => onChange('phoneNumber', e)}
            onBlur={() => checkOnUnfocus('phoneNumber', phoneNumber)}
            label="Celular"
            error={errors.phoneNumber}
            required
          />
          <RadioButtons
            title="Sexo"
            options={['Masculino', 'Feminino']}
            value={sex}
            onChange={(e) => onChangeSex(e)}
            required
          />
          <FormInput
            maxLength={20}
            type="text"
            value={insuranceMask(insuranceNumber)}
            onChange={(e) => onChange('insuranceNumber', e)}
            label="Número da carteirinha"
            onBlur={() => checkOnUnfocus('insuranceNumber', onlyNumbersMask(insuranceNumber))}
            error={errors.insuranceNumber}
          />
          <RadioButtons
            title="Tipo de Plano"
            options={['Particular', 'Empresarial']}
            value={insuranceType}
            onChange={(e) => onChangeInsuranceType('insuranceType', e)}
          />
          <RequiredFields />
        </FormFields>
      </Section>
      <ButtonContainer>
        <SaveButton
          onClick={() => handleClickSave()}
          disabled={validateUser(beneficiary) || loading}
        >
          {loading ? <Loading /> : 'Salvar'}
        </SaveButton>
      </ButtonContainer>
    </Container>
  )
}

AddBeneficiaryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setClose: PropTypes.func.isRequired,
  saveBeneficiary: PropTypes.func.isRequired,
  company: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
  }),
  programme: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
  }),
}

AddBeneficiaryModal.defaultProps = {
  company: null,
  programme: null,
}

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    saveBeneficiary: saveBeneficiaryAction,
  },
  dispatch
)

export default connect(null, mapDispatchToProps)(AddBeneficiaryModal)
